/**
 * Типы и (де)кодирование WS-протокола kikayoo (realtime-protocol.md).
 *
 * Любое сообщение — это JSON-конверт `{ "type": string, "payload": object }`.
 * Здесь он типизирован дискриминированными объединениями по полю `type`, что даёт
 * исчерпывающую проверку в switch без `any`. Источник истины по полям —
 * `server/internal/game/wire.go` (Go-сторона), здесь отражён 1:1.
 */

/** Роль клиента в комнате (поле join.role / joined.role). */
export type Role = 'host' | 'player';

/**
 * Фаза конечного автомата комнаты (state.phase). Значения — из
 * `server/internal/game/engine.go`: lobby → question_active → question_closed → finished.
 */
export type Phase = 'lobby' | 'question_active' | 'question_closed' | 'finished';

// ─── Полиморфные типы вопросов (Фаза 2, realtime-protocol.md §8) ──────────────

/**
 * Дискриминатор типа вопроса. Отсутствующее/пустое значение трактуется как
 * `"choice"` (обратная совместимость со старыми клиентами/квизами, протокол §8).
 * multi — мультивыбор с частичным зачётом (Фаза 14, эпик C); run — «Беги к
 * углу» (Фаза 14, эпик A+B).
 */
export type Kind = 'choice' | 'poll' | 'open' | 'match' | 'order' | 'word' | 'multi' | 'run';

/** Все известные значения Kind (для валидации входящих данных и итерации в UI). */
export const KINDS: readonly Kind[] = [
  'choice',
  'poll',
  'open',
  'match',
  'order',
  'word',
  'multi',
  'run',
];

/** Человекочитаемые названия типов — для бейджа/подписи на экране. */
export const KIND_LABEL: Record<Kind, string> = {
  choice: 'Выбор варианта',
  poll: 'Опрос',
  open: 'Открытый ответ',
  match: 'Соответствие',
  order: 'Упорядочивание',
  word: 'Облако слов',
  multi: 'Мультивыбор',
  run: 'Беги к углу',
};

/**
 * kindData вопроса — type-специфичное определение **без** правильного ответа
 * (анти-чит, протокол §8.1). choice/poll несут варианты; open — лимит длины;
 * match — два столбца (без верной связи); order — перемешанные элементы
 * (без верного порядка).
 */
export interface ChoiceKindData {
  options: string[];
}
export interface PollKindData {
  options: string[];
}
export interface OpenKindData {
  /** Максимальная длина текстового ответа (подсказка ограничения ввода). */
  maxLen?: number;
}
export interface MatchKindData {
  left: string[];
  right: string[];
}
export interface OrderKindData {
  items: string[];
}
/**
 * kindData word-вопроса (облако слов, И8 Фазы 10): только лимит длины ввода.
 * Правильного ответа нет — прятать нечего (анти-чит тривиален).
 */
export interface WordKindData {
  /** Максимальная длина слова игрока (сервер нормализует 0 → 40, потолок 200). */
  maxLen?: number;
}
export type KindData =
  | ChoiceKindData
  | PollKindData
  | OpenKindData
  | MatchKindData
  | OrderKindData
  | WordKindData
  | MultiKindData
  | RunKindData;

/**
 * kindData multi-вопроса (Фаза 14, эпик C): только варианты — индексы
 * правильных НЕ попадают на провод (анти-чит, приходят в reveal).
 */
export interface MultiKindData {
  options: string[];
}

/**
 * kindData run-вопроса «Беги к углу» (Фаза 14, эпик A; спека §3.1):
 * {corners, seed, params} БЕЗ correctIndex (анти-чит — правильный угол уходит
 * только в reveal). seed — int64 из [0, 2^53) (Number-безопасно), генерируется
 * сервером на каждый StartQuestion и кэшируется до close (reconnect — тот же
 * seed). params — фикс-константы физики (дубль RunPhysics из
 * server/internal/game/run.go; TS-копия — components/views/runEngine.ts).
 *
 * Открытые квизы /q/<slug> (решение Д §3.6): сервер присылает заглушку
 * {corners, soloUnsupported:true} — без seed/params; клиент показывает
 * «тип недоступен в соло-режиме» (OpenQuizRun).
 *
 * Редизайн 0.14.1 (решение владельца 2026-10-20): dead/diedAtMs/deadX/deadY —
 * персонально для переподключившегося ПОСЛЕ гибели игрока («ты наблюдатель»,
 * additive: живым поля не пишутся — kindData побайтово как в v0.14.0).
 * deadX/deadY — место гибели (нормализованные 0..1): reconnect-пятно на
 * месте гибели, а не в центре плитки (ведущий дополнительно видит места
 * гибели в positions dead-флагах и reveal dead-листе).
 *
 * 0.14.3 (S1 — единая серверная временная база): startedAtMs/now — серверные
 * Unix-мс (int64). Клиент при появлении kindData считает offset = now −
 * Date.now() и ведёт симуляцию дронов от startedAtMs в серверном времени —
 * фазы у ведущего и игроков совпадают (диагноз D1 плана 0.14.3: своя база
 * performance.now() у каждого клиента → фазовый сдвиг в сотни мс).
 * startedAtMs — тот же момент, что top-level question.startedAt (одни серверные
 * часы), now — свежее время сборки kindData (у reconnect-пакетов больше).
 * Сервер шлёт оба поля БЕЗ omitempty (для run — всегда); опциональность здесь —
 * превью редактора/старые пакеты → клиент на локальных часах (runEngine.makeRunClock).
 */
export interface RunKindData {
  /** Тексты 4 углов плитки (0 — левый-верхний, по часовой). */
  corners: string[];
  /** Seed дрон-спавнера (детерминизм у всех клиентов вопроса); опц. в соло-заглушке. */
  seed?: number;
  /** Фикс-константы физики; опц. (клиент fallback на RUN_PHYSICS из runEngine.ts). */
  params?: RunParams;
  /** Маркер соло-заглушки открытых квизов (run в соло не поддерживается). */
  soloUnsupported?: boolean;
  /** 0.14.1: игрок погиб от дрона — сразу наблюдатель (только погибшему при reconnect). */
  dead?: boolean;
  /** Момент гибели (мс от старта вопроса); смысл только при dead. */
  diedAtMs?: number;
  /** Место гибели X (нормализованное 0..1 — куда рисовать reconnect-пятно). */
  deadX?: number;
  /** Место гибели Y (нормализованное 0..1 — куда рисовать reconnect-пятно). */
  deadY?: number;
  /** 0.14.3 (S1): серверное Unix-мс старта вопроса (база симуляции дронов). */
  startedAtMs?: number;
  /** 0.14.3 (S1): серверное Unix-мс сборки kindData (клиентский offset-расчёт). */
  now?: number;
}

/**
 * Фикс-константы физики/дронов run-вопроса — зеркальный тип серверного
 * game.RunParams (server/internal/game/run.go). Единицы нормализованы: доли
 * плитки 0..1, скорость — плиток/сек, время — мс.
 */
export interface RunParams {
  moveSpeed: number;
  playerRadius: number;
  droneRadius: number;
  firstDroneMs: number;
  spawnIntervalStartMs: number;
  spawnIntervalEndMs: number;
  droneSpeedStart: number;
  droneSpeedEnd: number;
  stunMs: number;
}

/**
 * data ответа игрока — type-специфичная часть `answer` (протокол §8.2).
 * Для open/match/order поле `data` обязательно; без него сервер вернёт
 * `invalid_payload`.
 */
export interface OpenAnswerData {
  text: string;
}
export interface MatchPair {
  left: string;
  right: string;
}
export interface MatchAnswerData {
  pairs: MatchPair[];
}
export interface OrderAnswerData {
  /** Перестановка индексов `kindData.items` — порядок, заданный игроком. */
  order: number[];
}
/** Ответ word-вопроса: свободное слово/фраза (И8 Фазы 10). */
export interface WordAnswerData {
  word: string;
}
/** Ответ multi-вопроса: выбранные индексы вариантов (Фаза 14, эпик C). */
export interface MultiAnswerData {
  selected: number[];
}
export type AnswerData =
  OpenAnswerData | MatchAnswerData | OrderAnswerData | WordAnswerData | MultiAnswerData;

/**
 * reveal-данные раскрытия правильного ответа (протокол §8.3). Для `poll`
 * событие `reveal` сервером не рассылается (правильного ответа нет).
 */
export interface OpenReveal {
  /** Допустимые (эталонные) текстовые ответы. */
  accept: string[];
}
export interface MatchReveal {
  /** Эталонные пары соответствия. */
  pairs: MatchPair[];
}
export interface OrderReveal {
  /** Верный порядок — перестановка индексов `kindData.items`. */
  correctOrder: number[];
}
/** Одна строка частотного облака: слово и число упоминаний. */
export interface WordCountEntry {
  word: string;
  count: number;
}
/**
 * Reveal word-вопроса — финальное частотное облако (агрегат ответов, а не
 * эталон; И8 Фазы 10). Сервер сортирует count DESC → word ASC.
 */
export interface WordReveal {
  words: WordCountEntry[];
}
export type RevealData =
  OpenReveal | MatchReveal | OrderReveal | WordReveal | MultiReveal | RunDeadReveal;

/**
 * Reveal multi-вопроса: индексы правильных вариантов (reveal.correct; дубль
 * top-level `correctOptions` — см. RevealPayload). Фронт читает оба канала.
 */
export interface MultiReveal {
  correct: number[];
}

/**
 * Унифицированный ответ игрока на уровне UI (диспетчер `QuestionView` и его
 * под-view). Каждый под-view вызывает `onAnswer` со своим вариантом; экран
 * игрока отображает это в wire-`AnswerPayload` через `makeAnswerPayload`.
 */
export type PlayerAnswer =
  | { kind: 'choice'; option: number }
  | { kind: 'poll'; option: number }
  | { kind: 'open'; data: OpenAnswerData }
  | { kind: 'match'; data: MatchAnswerData }
  | { kind: 'order'; data: OrderAnswerData }
  | { kind: 'word'; data: WordAnswerData }
  | { kind: 'multi'; data: MultiAnswerData };

// ─── Сообщения Клиент → Сервер ────────────────────────────────────────────────

/** payload сообщения `join`: подключение к комнате по PIN. */
export interface JoinPayload {
  /** Идентификатор комнаты (совпадает с PIN сессии). */
  pin: string;
  /** Ник участника (обязателен для role=player; ведущему не нужен). */
  nick?: string;
  /** role=host — ведущий; пусто/отсутствует — участник. */
  role?: Role;
  /**
   * Device-токен игрока (Фаза 13, Эпик A1, additive): из предыдущего
   * `joined.playerToken` — клиент хранит в localStorage `kikayoo:ptoken:<pin>`
   * и шлёт в каждом join (включая реконнекты); сервер по нему восстанавливает
   * слот (ник payload игнорируется). Отсутствует — первый вход/legacy-клиент.
   */
  token?: string;
}

/** payload сообщения `answer`: идемпотентный ответ на текущий вопрос. */
export interface AnswerPayload {
  /** Номер вопроса (1-based, совпадает с answers.question_id). */
  questionId: number;
  /** Индекс выбранного варианта (0-based) — для choice/poll. Опускается для open/match/order/multi. */
  option?: number;
  /** Тип ответа (лишь подсказка; сервер маршрутизирует по типу текущего вопроса — протокол §8.2). */
  kind?: Kind;
  /** Type-специфичный ответ; обязателен для open/match/order/multi (без data → `invalid_payload`). */
  data?: AnswerData;
}

/** Пустой payload для host-команд next/close. */
export type EmptyPayload = Record<string, never>;

/**
 * payload события `pos` (Фаза 14, эпик A2; спека §3.3): позиция игрока на
 * плитке run-вопроса. Клиент шлёт ~3 Гц (серверный rate-guard 10 Гц — не
 * превышаем), нормализованные координаты 0..1. Прямых ответов pos не несёт:
 * зачёт — серверный снапшот при close (§3.2), классический answer у run
 * запрещён.
 */
export interface PosPayload {
  /** Номер текущего run-вопроса (1-based). */
  questionId: number;
  /** Нормализованная координата 0..1 (доля плитки). */
  x: number;
  y: number;
}

/**
 * payload host-команды `start` (И4, Фаза 10): опции проведения сессии.
 * Поля опциональны — старый клиент/выключенный режим шлёт `{}` (обратная
 * совместимость, протокол §7/§10). Все опции фиксируются сервером при старте
 * (в фазе lobby) и по ходу игры не меняются.
 */
export interface StartPayload {
  /** Включён режим «счёт после каждого вопроса»: после закрытия вопроса игроки
   *  видят экран топа счёта; переход — host-controlled (кнопка next). */
  scorePause?: boolean;
  /** Закрыть вход НОВЫМ никам после старта (Фаза 13, C1): reconnect слота
   *  (токен/ник) всегда разрешён; опоздавший с новым ником → `join_closed`. */
  lockAfterStart?: boolean;
  /** Строгий ник-режим (Фаза 13, C1): занятый ник при join → `nick_taken`
   *  (экран подсказок A2b) вместо legacy-resume по нику. */
  strictNick?: boolean;
  /** Авто-переход к следующему вопросу (Фаза 16, A): после reveal серверный
   *  таймер (limits.auto_next_delay_seconds, фиксируется при старте сессии)
   *  сам выполняет «дальше» — следующий вопрос или финиш. Ручной next хоста
   *  раньше таймера отменяет его; со scorePause не взаимоисключается. */
  autoNext?: boolean;
}

/**
 * payload сообщения `rename`: смена ника в фазе lobby (И5). Игрок шлёт без
 * `playerId` → меняет свой ник; ведущий шлёт с `playerId` чужого игрока → меняет
 * ему ник и фиксирует его (nickLocked). Для игрока поле `playerId` сервером
 * игнорируется (только свой ник).
 */
export interface RenamePayload {
  /** Цель переименования; отсутствует/null = свой ник (игрок). Задаёт хост. */
  playerId?: number | null;
  /** Новый ник (санитайзится и валидируется сервером). */
  nick: string;
}

/**
 * payload host-команды `kick` (Фаза 15, эпик B1): удалить участника из сессии.
 * Цель — СЛОТ (`roster.entry.playerId`), не ник-канон: блокируется возврат именно
 * этого слота (и по токену, и по нику); хоста и несуществующий слот кикнуть
 * нельзя (`unknown_player`), повторный kick — идемпотентный no-op. Кик в лобби
 * и в активном вопросе — одинаковое поведение.
 */
export interface KickPayload {
  /** playerID слота-цели (players_in_session.id, из roster-записи). */
  playerId: number;
}

/**
 * Объединение всех сообщений от клиента к серверу. Дискриминант — `type`.
 * Host-команды (`start`/`next`/`close`) принимает только клиент с role=host.
 */
export type ClientMessage =
  | { type: 'join'; payload: JoinPayload }
  | { type: 'answer'; payload: AnswerPayload }
  | { type: 'pos'; payload: PosPayload }
  | { type: 'start'; payload: StartPayload }
  | { type: 'next'; payload: EmptyPayload }
  | { type: 'close'; payload: EmptyPayload }
  | { type: 'rename'; payload: RenamePayload }
  | { type: 'kick'; payload: KickPayload };

// ─── Сообщения Сервер → Клиент ────────────────────────────────────────────────

/** payload события `joined`: подтверждение входа в комнату. */
export interface JoinedPayload {
  /** ID сессии. */
  sessionId: number;
  /** Роль зашедшего. */
  role: Role;
  /** Кто зашёл (id игрока + ник). */
  you: { id: number; nick: string };
  /**
   * Device-токен игрока (Фаза 13, Эпик A1, additive): выдаётся ТОЛЬКО при
   * создании нового слота (и при legacy-resume по нику); при reconnect по
   * токену не пере-выдаётся — клиент его уже хранит. Отсутствует — resume
   * прежнего слота или старый сервер; токен на устройстве не трогаем.
   */
  playerToken?: string;
}

/**
 * payload события `question`: новый вопрос. Правильный вариант сюда НЕ входит
 * (анти-чит). Таймер — серверный: `durationMs` + `startedAt` (Unix-мс) дают
 * клиенту построить обратный отсчёт для UX, но истина — на сервере.
 */
export interface QuestionPayload {
  questionId: number;
  title: string;
  /** Варианты для choice/poll (индексы = AnswerPayload.option); для прочих типов отсутствует. */
  options?: string[];
  /** Длительность вопроса в миллисекундах. */
  durationMs: number;
  /** Unix-мс момента старта по серверным часам. */
  startedAt: number;
  /** 0-based индекс вопроса в квизе. */
  index: number;
  /** Всего вопросов в квизе. */
  total: number;
  /**
   * Дискриминатор типа вопроса; отсутствие трактуется как "choice" (обратная
   * совместимость). С Фазы 14 — string (§4 спеки): незнакомый kind НЕ отбрасывает
   * payload (UnknownKindView), известные проверяются через KINDS/questionKind.
   */
  kind?: string;
  /** Type-специфичное определение **без** правильного ответа (анти-чит, протокол §8.1). */
  kindData?: KindData;
  /**
   * URL изображения вопроса (например `/uploads/<hex>.jpg`); опц. — сервер шлёт, если
   * в вопросе задано image (И2/И3). Отсутствие/null — у вопроса нет картинки.
   */
  image?: string;
  /**
   * Многострочный блок кода вопроса (Фаза 16, B1, omitempty): ровно один на
   * вопрос, отдаётся как текст (код — не секрет, в т.ч. открытые квизы).
   * Отсутствие — у вопроса нет кода, CodeBlock не рендерится.
   */
  code?: string;
  /**
   * Язык блока кода из whitelist 14 значений (канон — game.CodeLangs на
   * сервере, realtime-protocol.md §8.7). Отсутствует/пусто при заданном code
   * => plaintext (без подсветки); «plaintext» может приходить явно.
   */
  codeLang?: string;
}

/** payload события `stats`: живая статистика ответов. */
export interface StatsPayload {
  questionId: number;
  /** Голоса по вариантам — присутствует только для choice/poll; для open/match/order отсутствует. */
  counts?: number[];
  /** Всего ответивших на текущий вопрос (все типы; И7, опц-улучшение). даёт «N из M»
   *  даже для open/match/order, где counts нет. Сервер опускает поле при 0 (omitempty). */
  answered?: number;
  /**
   * Живое частотное облако word-вопроса (И8 Фазы 10): нормализованные частоты
   * ответов, растёт по дебаунс-рассылке stats. Присутствует только для kind=word
   * (для прочих типов сервер поле опускает). Финальное облако — в reveal.words.
   */
  words?: WordCountEntry[];
  kind?: Kind;
}

/** payload события `reveal`: раскрытие правильного ответа после закрытия вопроса. */
export interface RevealPayload {
  questionId: number;
  /** Индекс верного варианта для choice (0-based); для прочих типов отсутствует. */
  correctOption?: number;
  /**
   * Индексы верных вариантов для multi (0-based, отсортированы; Фаза 14, спека
   * М5). Дубль reveal.correct (см. MultiReveal) — фронт читает оба канала.
   */
  correctOptions?: number[];
  kind?: Kind;
  /** Type-специфичное раскрытие для open/match/order/multi (НЕ приходит для poll). */
  reveal?: RevealData;
}

/** Строка рейтинга: ник и текущий счёт. */
export interface ScoreboardEntry {
  nick: string;
  score: number;
}

/** payload события `scoreboard`: текущий рейтинг (можно слать многократно).
 *
 * Фаза 11 (И6прот): при числе игроков >25 сервер шлёт только топ-25 и заполняет
 * `total` — полное число игроков в рейтинге; отсутствие `total` (=undefined/0)
 * означает, что передан полный список. Единственный способ клиента понять
 * «топ это или всё». */
export interface ScoreboardPayload {
  entries: ScoreboardEntry[];
  /** Полное число игроков в рейтинге; присутствует только при топ-K (N>K). */
  total?: number;
}

/** payload события `placement`: место игрока в текущем рейтинге (только N>K). */
export interface PlacementPayload {
  /** Место 1-based по сортировке рейтинга (score DESC, nick ASC). */
  rank: number;
  /** Актуальный счёт игрока. */
  score: number;
}

/**
 * Статус подключения слота (Фаза 15, A1, additive): `active` — подключён
 * (дефолт; в JSON опускается), `dropped` — WS слота закрыт (readPump-детект),
 * слот остаётся в игре, reconnect возвращает active. `kicked`-слоты из
 * roster-рассылок исключены целиком (терминальный статус живёт только в REST-итогах).
 */
export type RosterStatus = 'active' | 'dropped';

/** Строка доски участников лобби (событие `roster`, И5): только не-ведущие игроки. */
export interface RosterEntry {
  /** Идентификатор игрока (players_in_session.id) — нужен хосту для rename/kick. */
  playerId: number;
  /** Текущий ник. */
  nick: string;
  /** true, если ведущий уже переименовал этого игрока — сам он ник не сменит. */
  nickLocked: boolean;
  /**
   * Статус подключения слота (Фаза 15, A1, omitempty): отсутствие = active
   * (старые серверы поле не шлют). Гвард пропускает любую строку — незнакомый
   * статус forward-compat трактуется UI как active (сравнение с 'dropped').
   */
  status?: RosterStatus;
  /** Unix-мс момента отвала (readPump-детект обрыва WS); 0/отсутствие — не отваливался. */
  droppedAt?: number;
  /** Unix-мс момента возврата после отвала (reconnect); только если слот отваливался. */
  returnedAt?: number;
}

/** payload события `roster`: доска присоединившихся участников лобби (И5). */
export interface RosterPayload {
  entries: RosterEntry[];
}

/** Позиция одного игрока в пакете positions (server game.PlayerPos 1:1). */
export interface PlayerPos {
  /** playerID (players_in_session.id). */
  id: number;
  /** Ник игрока (для подписи дота у ведущего). */
  nick: string;
  /** Нормализованные координаты плитки 0..1. */
  x: number;
  y: number;
  /**
   * 0.14.1 (additive, omitempty): игрок погиб от дрона — ведущий рисует пятно
   * на месте гибели (x/y — последняя позиция, дальше не обновляется: pos
   * мёртвых игнорируются релеем). Живым поля отсутствуют.
   */
  dead?: boolean;
  /** Момент гибели (мс от старта вопроса); смысл только при dead. */
  diedAtMs?: number;
}

/** Запись о погибшем в reveal.run dead-листе (0.14.1; server RunDeadPlayer 1:1). */
export interface RunDeadPlayer {
  /** playerID погибшего. */
  playerId: number;
  /** Ник (для плашки/подписи у ведущего). */
  nick: string;
  /** Момент гибели (мс от старта вопроса; сортировка списка по нему). */
  diedAtMs: number;
  /** Место гибели — нормализованные координаты (куда рисовать пятно). */
  x: number;
  y: number;
}

/** Reveal-данные run: {dead:[…]} — список погибших ведущему (additive, 0.14.1). */
export interface RunDeadReveal {
  dead: RunDeadPlayer[];
}

/**
 * payload события `run_dead` (0.14.3, S4 — dead-пуш в реальном времени;
 * server game.RunDeadPayload 1:1): сервер шлёт его ПОГИБШЕМУ игроку в момент
 * фиксации гибели от дрона — клиент мгновенно взрывается и становится
 * наблюдателем, даже если локальный детект не сработал из-за фазового сдвига
 * (лечит «взорван на сервере, но стоит и бегает у игрока»). Персональное
 * (не broadcast); идемпотентно с локальным детектом RunView (first-wins) и с
 * kindData.dead при reconnect — run_dead только онлайн-ускоритель.
 */
export interface RunDeadPayload {
  /** Номер run-вопроса (1-based). */
  questionId: number;
  /** Момент гибели (мс от старта вопроса; серверная симуляция). */
  diedAtMs: number;
  /** Место гибели X (0..1 — последний принятый pos). */
  x: number;
  /** Место гибели Y (0..1). */
  y: number;
}

/**
 * payload события `positions` (сервер→ведущий, ~5 Гц; спека §3.3): коалесированный
 * снапшот последних позиций всех игроков активного run-вопроса. T — серверное
 * Unix-мс сборки пакета. Игроки чужие позиции НЕ получают; финальный пакет при
 * close замораживает доты ведущего до reveal.
 */
export interface PositionsPayload {
  questionId: number;
  t: number;
  players: PlayerPos[];
}

/**
 * payload события `roster_delta`: единичное изменение доски лобби. `join`/
 * `rename` приходят при N>K вместо полного roster (Фаза 11, И6прот); `update` —
 * статусная дельта Фазы 15 (A1): broadcast ВСЕМ при отвале (`dropped`) и
 * возврате (`active`) слота, в любой фазе комнаты.
 */
export interface RosterDeltaPayload {
  /** Вид изменения; неизвестные значения игнорируются клиентом (forward-compat). */
  op: 'join' | 'rename' | 'update';
  entry: RosterEntry;
}

/**
 * payload события `state`: снапшот комнаты (после join / reconnect).
 * questionId/secondsLeft присутствуют только в активной фазе вопроса.
 */
export interface StatePayload {
  phase: Phase;
  questionId?: number;
  secondsLeft?: number;
  totalQuestions: number;
  participants: number;
  /**
   * Включён режим «счёт после каждого вопроса» (И4, Фаза 10). Display-флаг:
   * сервер шлёт его во всех state (включая join опоздавших), чтобы
   * reconnect-клиент восстановил экран топа. Отсутствует = выключен.
   */
  scorePause?: boolean;
  /**
   * Вход новым никам закрыт после старта (host start-payload, Фаза 13, C1).
   * Отсутствует = выключен (сервер шлёт omitempty).
   */
  lockAfterStart?: boolean;
  /**
   * Строгий ник-режим сессии (host start-payload, Фаза 13, C1) — эффективное
   * значение. Отсутствует = выключен.
   */
  strictNick?: boolean;
  /**
   * Авто-переход к следующему вопросу после reveal (host start-payload,
   * Фаза 16, A): display-флаг по образцу scorePause — сервер шлёт его во всех
   * state, reconnect-клиенты видят режим. Отсутствует = выключен.
   */
  autoNext?: boolean;
  /**
   * Per-room счётчик уникальных игроков (созданные слоты, Фаза 15, C1/C2):
   * источник live-панели ведущего «Игроков: N». omitempty ⇒ до первого входа.
   */
  playersUnique?: number;
  /**
   * Per-room счётчик переподключений (resume-входы по device-токену/legacy-нику,
   * Фаза 15, C1/C2). omitempty ⇒ 0 опускается (панель рендерит 0 по отсутствию).
   */
  reconnects?: number;
  /**
   * Уникальные отсортированные codeLang вопросов квиза (Фаза 16, B1,
   * omitempty): клиент по ним предзагружает ленивые грамматики подсветки
   * (B2) при входе в сессию — игроки получают вопросы по одному и не знают
   * состав квиза заранее. Явный "plaintext" включён; поле отсутствует для
   * квиза без кода (чанки не грузятся). Языки ≠ ответы — утечки нет.
   */
  langs?: string[];
}

/** payload события `finished`: сессия завершена, итоги доступны через REST. */
export interface FinishedPayload {
  sessionId: number;
}

/** payload события `error`: код + человекочитаемое сообщение. */
export interface ErrorPayload {
  code: string;
  message: string;
}

/**
 * Объединение всех сообщений от сервера к клиенту. Дискриминант — `type`.
 * Неизвестные `type` игнорируются клиентом (forward compatibility, протокол §7).
 */
export type ServerMessage =
  | { type: 'joined'; payload: JoinedPayload }
  | { type: 'question'; payload: QuestionPayload }
  | { type: 'stats'; payload: StatsPayload }
  | { type: 'reveal'; payload: RevealPayload }
  | { type: 'scoreboard'; payload: ScoreboardPayload }
  | { type: 'state'; payload: StatePayload }
  | { type: 'roster'; payload: RosterPayload }
  | { type: 'placement'; payload: PlacementPayload }
  | { type: 'roster_delta'; payload: RosterDeltaPayload }
  | { type: 'positions'; payload: PositionsPayload }
  | { type: 'run_dead'; payload: RunDeadPayload }
  | { type: 'finished'; payload: FinishedPayload }
  | { type: 'error'; payload: ErrorPayload };

/** Все известные типы серверных событий (для отображения/отладки). */
export type ServerEventType = ServerMessage['type'];

// ─── Коды ошибок (realtime-protocol.md §3 / wire.go ErrCode*) ─────────────────

export const ErrorCode = {
  RoomNotFound: 'room_not_found',
  AlreadyAnswered: 'already_answered',
  InvalidPayload: 'invalid_payload',
  NotActive: 'not_active',
  StaleQuestion: 'stale_question',
  InvalidOption: 'invalid_option',
  NoMoreQuestions: 'no_more_questions',
  InvalidPhase: 'invalid_phase',
  UnknownPlayer: 'unknown_player',
  AlreadyFinished: 'already_finished',
  // И5: лимит игроков в комнате достигнут (defensive cap MAX_PLAYERS_PER_ROOM).
  RoomFull: 'room_full',
  // И5 (lobby): ник заблокирован ведущим / уже занят в комнате.
  NickLocked: 'nick_locked',
  DuplicateNick: 'duplicate_nick',
  // Фаза 13 (A1/C1): ник занят другим игроком при strictNick — join нового
  // слота отклонён; клиент показывает экран с подсказками (A2b).
  NickTaken: 'nick_taken',
  // Фаза 13 (B1): сокет замещён новым подключением этого слота — сервер
  // закрывает соединение; клиент молча завершает сессию без реконнекта.
  Superseded: 'superseded',
  // Фаза 13 (C1): вход новым ником после старта сессии закрыт (lockAfterStart).
  JoinClosed: 'join_closed',
  // Фаза 15 (B1): ведущий удалил участника (host-команда kick). Терминальный
  // для слота код: повторный join слота (токен/ник) отклоняется тем же кодом,
  // автореконнект ws-слоя по нему не выполняется, игроку — конечный экран.
  Kicked: 'kicked',
} as const;

/** Внутреннее представление сырого конверта до типизации payload. */
interface RawEnvelope {
  type: string;
  // payload — объект; на этапе разбора ещё нетипизирован.
  payload: unknown;
}

/** Коды фаз комнаты (для отображения человеку). */
export const PHASE_LABEL: Record<Phase, string> = {
  lobby: 'Ожидание игроков',
  question_active: 'Идёт вопрос',
  question_closed: 'Результаты',
  finished: 'Игра окончена',
};

// ─── Кодирование (клиент → сервер) ────────────────────────────────────────────

/**
 * Сериализует клиентское сообщение в готовую к отправке JSON-строку конверта.
 * payload всегда объект (для host-команд — пустой `{}`).
 */
export function encodeMessage(message: ClientMessage): string {
  return JSON.stringify({ type: message.type, payload: message.payload });
}

// ─── Декодирование (сервер → клиент) ──────────────────────────────────────────

/** Парсит сырую строку конверта в типизированное серверное сообщение. */
export function decodeMessage(data: string): ServerMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  return parseServerMessage(parsed);
}

/**
 * Преобразует уже разобранный JSON в ServerMessage. Возвращает null для
 * неизвестных `type` или некорректной структуры (клиент их игнорирует).
 */
export function parseServerMessage(value: unknown): ServerMessage | null {
  if (!isPlainEnvelope(value)) {
    return null;
  }
  switch (value.type) {
    case 'joined':
      return isJoinedPayload(value.payload) ? { type: 'joined', payload: value.payload } : null;
    case 'question':
      return isQuestionPayload(value.payload) ? { type: 'question', payload: value.payload } : null;
    case 'stats':
      return isStatsPayload(value.payload) ? { type: 'stats', payload: value.payload } : null;
    case 'reveal':
      return isRevealPayload(value.payload) ? { type: 'reveal', payload: value.payload } : null;
    case 'scoreboard':
      return isScoreboardPayload(value.payload)
        ? { type: 'scoreboard', payload: value.payload }
        : null;
    case 'state':
      return isStatePayload(value.payload) ? { type: 'state', payload: value.payload } : null;
    case 'roster':
      return isRosterPayload(value.payload) ? { type: 'roster', payload: value.payload } : null;
    case 'placement':
      return isPlacementPayload(value.payload)
        ? { type: 'placement', payload: value.payload }
        : null;
    case 'roster_delta':
      return isRosterDeltaPayload(value.payload)
        ? { type: 'roster_delta', payload: value.payload }
        : null;
    case 'positions':
      // Фаза 14 (эпик A2): коалесированный пакет позиций run-вопроса — только
      // ведущему; игроки сообщение не получают, но декодер общий.
      return isPositionsPayload(value.payload)
        ? { type: 'positions', payload: value.payload }
        : null;
    case 'run_dead':
      // 0.14.3 (S4): персональный dead-пуш погибшему игроку run-вопроса.
      return isRunDeadPayload(value.payload) ? { type: 'run_dead', payload: value.payload } : null;
    case 'finished':
      return isFinishedPayload(value.payload) ? { type: 'finished', payload: value.payload } : null;
    case 'error':
      return isErrorPayload(value.payload) ? { type: 'error', payload: value.payload } : null;
    default:
      // Неизвестный тип — forward compatibility (протокол §7).
      return null;
  }
}

// ─── Тайп-гарды payload ───────────────────────────────────────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isString(v: unknown): v is string {
  return typeof v === 'string';
}

function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function isNumberArray(v: unknown): v is number[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'number' && Number.isFinite(x));
}

function isKind(v: unknown): v is Kind {
  return typeof v === 'string' && (KINDS as readonly string[]).includes(v);
}

function isPlainEnvelope(v: unknown): v is RawEnvelope {
  return isRecord(v) && isString(v.type) && 'payload' in v;
}

function isRole(v: unknown): v is Role {
  return v === 'host' || v === 'player';
}

function isJoinedPayload(v: unknown): v is JoinedPayload {
  if (!isRecord(v)) return false;
  return (
    isNumber(v.sessionId) &&
    isRole(v.role) &&
    isRecord(v.you) &&
    isNumber(v.you.id) &&
    isString(v.you.nick) &&
    (v.playerToken === undefined || isString(v.playerToken))
  );
}

function isQuestionPayload(v: unknown): v is QuestionPayload {
  if (!isRecord(v)) return false;
  return (
    isNumber(v.questionId) &&
    isString(v.title) &&
    (v.options === undefined || isStringArray(v.options)) &&
    isNumber(v.durationMs) &&
    isNumber(v.startedAt) &&
    isNumber(v.index) &&
    isNumber(v.total) &&
    // §4 спеки Фазы 14: kind — любая строка. Незнакомый kind НЕ отбрасывает
    // payload (иначе вопрос теряется целиком) — он деградирует в UnknownKindView
    // через questionKind() → 'unknown'. Отсутствие kind = legacy choice.
    (v.kind === undefined || isString(v.kind)) &&
    (v.kindData === undefined || isRecord(v.kindData)) &&
    (v.image === undefined || isString(v.image)) &&
    // Фаза 16 (B1, additive): блок кода вопроса — code текстом, codeLang из
    // whitelist (состав не гардим здесь — реестр грамматик в languages.ts).
    (v.code === undefined || isString(v.code)) &&
    (v.codeLang === undefined || isString(v.codeLang))
  );
}

function isWordCountEntry(v: unknown): v is WordCountEntry {
  return isRecord(v) && isString(v.word) && isNumber(v.count);
}

function isStatsPayload(v: unknown): v is StatsPayload {
  if (!isRecord(v)) return false;
  return (
    isNumber(v.questionId) &&
    (v.counts === undefined || isNumberArray(v.counts)) &&
    (v.answered === undefined || isNumber(v.answered)) &&
    (v.words === undefined || (Array.isArray(v.words) && v.words.every(isWordCountEntry))) &&
    (v.kind === undefined || isKind(v.kind))
  );
}

function isRevealPayload(v: unknown): v is RevealPayload {
  if (!isRecord(v)) return false;
  return (
    isNumber(v.questionId) &&
    (v.correctOption === undefined || isNumber(v.correctOption)) &&
    (v.kind === undefined || isKind(v.kind)) &&
    (v.reveal === undefined || isRecord(v.reveal))
  );
}

function isScoreboardEntry(v: unknown): v is ScoreboardEntry {
  return isRecord(v) && isString(v.nick) && isNumber(v.score);
}

function isScoreboardPayload(v: unknown): v is ScoreboardPayload {
  if (!isRecord(v)) return false;
  return (
    Array.isArray(v.entries) &&
    v.entries.every(isScoreboardEntry) &&
    (v.total === undefined || isNumber(v.total))
  );
}

function isRosterEntry(v: unknown): v is RosterEntry {
  return (
    isRecord(v) &&
    isNumber(v.playerId) &&
    isString(v.nick) &&
    typeof v.nickLocked === 'boolean' &&
    // Фаза 15 (A1, additive): статус слота и таймстампы отвала/возврата.
    // Статус — любая строка (незнакомое значение UI трактует как active).
    (v.status === undefined || isString(v.status)) &&
    (v.droppedAt === undefined || isNumber(v.droppedAt)) &&
    (v.returnedAt === undefined || isNumber(v.returnedAt))
  );
}

function isRosterPayload(v: unknown): v is RosterPayload {
  if (!isRecord(v)) return false;
  return Array.isArray(v.entries) && v.entries.every(isRosterEntry);
}

// Фаза 15 (A1): 'update' — статусная дельта слота (active/dropped).
const ROSTER_OPS: ReadonlySet<string> = new Set(['join', 'rename', 'update']);

function isPlacementPayload(v: unknown): v is PlacementPayload {
  if (!isRecord(v)) return false;
  return isNumber(v.rank) && isNumber(v.score);
}

function isPlayerPos(v: unknown): v is PlayerPos {
  return (
    isRecord(v) &&
    isNumber(v.id) &&
    isString(v.nick) &&
    isNumber(v.x) &&
    isNumber(v.y) &&
    (v.dead === undefined || typeof v.dead === 'boolean') &&
    (v.diedAtMs === undefined || isNumber(v.diedAtMs))
  );
}

function isPositionsPayload(v: unknown): v is PositionsPayload {
  return (
    isRecord(v) &&
    isNumber(v.questionId) &&
    isNumber(v.t) &&
    Array.isArray(v.players) &&
    v.players.every(isPlayerPos)
  );
}

function isRunDeadPayload(v: unknown): v is RunDeadPayload {
  return (
    isRecord(v) && isNumber(v.questionId) && isNumber(v.diedAtMs) && isNumber(v.x) && isNumber(v.y)
  );
}

function isRosterDeltaPayload(v: unknown): v is RosterDeltaPayload {
  if (!isRecord(v)) return false;
  return typeof v.op === 'string' && ROSTER_OPS.has(v.op) && isRosterEntry(v.entry);
}

const PHASES: ReadonlySet<string> = new Set([
  'lobby',
  'question_active',
  'question_closed',
  'finished',
]);

function isPhase(v: unknown): v is Phase {
  return typeof v === 'string' && PHASES.has(v);
}

function isStatePayload(v: unknown): v is StatePayload {
  if (!isRecord(v)) return false;
  return (
    isPhase(v.phase) &&
    (v.questionId === undefined || isNumber(v.questionId)) &&
    (v.secondsLeft === undefined || isNumber(v.secondsLeft)) &&
    isNumber(v.totalQuestions) &&
    isNumber(v.participants) &&
    (v.scorePause === undefined || typeof v.scorePause === 'boolean') &&
    (v.lockAfterStart === undefined || typeof v.lockAfterStart === 'boolean') &&
    (v.strictNick === undefined || typeof v.strictNick === 'boolean') &&
    // Фаза 16 (A): авто-переход после reveal (display-флаг, omitempty).
    (v.autoNext === undefined || typeof v.autoNext === 'boolean') &&
    // Фаза 15 (C2): per-room счётчики входов (omitempty, additive).
    (v.playersUnique === undefined || isNumber(v.playersUnique)) &&
    (v.reconnects === undefined || isNumber(v.reconnects)) &&
    // Фаза 16 (B1): языки блоков кода квиза (массив строк, omitempty).
    (v.langs === undefined || isStringArray(v.langs))
  );
}

function isFinishedPayload(v: unknown): v is FinishedPayload {
  return isRecord(v) && isNumber(v.sessionId);
}

function isErrorPayload(v: unknown): v is ErrorPayload {
  return isRecord(v) && isString(v.code) && isString(v.message);
}

// ─── Удобные тайп-гарды для switch по сообщению ──────────────────────────────

export function isJoined(m: ServerMessage): m is { type: 'joined'; payload: JoinedPayload } {
  return m.type === 'joined';
}

export function isQuestion(m: ServerMessage): m is { type: 'question'; payload: QuestionPayload } {
  return m.type === 'question';
}

export function isStats(m: ServerMessage): m is { type: 'stats'; payload: StatsPayload } {
  return m.type === 'stats';
}

export function isReveal(m: ServerMessage): m is { type: 'reveal'; payload: RevealPayload } {
  return m.type === 'reveal';
}

export function isScoreboard(
  m: ServerMessage,
): m is { type: 'scoreboard'; payload: ScoreboardPayload } {
  return m.type === 'scoreboard';
}

export function isState(m: ServerMessage): m is { type: 'state'; payload: StatePayload } {
  return m.type === 'state';
}

export function isRoster(m: ServerMessage): m is { type: 'roster'; payload: RosterPayload } {
  return m.type === 'roster';
}

export function isPlacement(
  m: ServerMessage,
): m is { type: 'placement'; payload: PlacementPayload } {
  return m.type === 'placement';
}

export function isRosterDelta(
  m: ServerMessage,
): m is { type: 'roster_delta'; payload: RosterDeltaPayload } {
  return m.type === 'roster_delta';
}

export function isFinished(m: ServerMessage): m is { type: 'finished'; payload: FinishedPayload } {
  return m.type === 'finished';
}

export function isError(m: ServerMessage): m is { type: 'error'; payload: ErrorPayload } {
  return m.type === 'error';
}

// ─── Помощники UX (таймер) ────────────────────────────────────────────────────

/**
 * Считает остаток ответа в секундах по серверным `startedAt`/`durationMs` и
 * локальному «сейчас». Отрицательный результат не выдаём — истина на сервере:
 * по истечении сервер сам пришлёт reveal/stats (realtime-protocol.md §5).
 */
export function secondsLeft(
  startedAt: number,
  durationMs: number,
  now: number = Date.now(),
): number {
  const remainingMs = startedAt + durationMs - now;
  return Math.max(0, Math.ceil(remainingMs / 1000));
}

// ─── Полиморфные хелперы и тайп-гарды (Фаза 2, протокол §8) ───────────────────

/**
 * Резолвит Kind вопроса с дефолтом "choice" (обратная совместимость, протокол
 * §8). Незнакомый серверу-клиенту kind (будущие типы, §4 спеки Фазы 14)
 * возвращает 'unknown' — QuestionView рендерит UnknownKindView.
 */
export function questionKind(q: QuestionPayload): Kind | 'unknown' {
  if (q.kind === undefined || q.kind === '') return 'choice';
  return (KINDS as readonly string[]).includes(q.kind) ? (q.kind as Kind) : 'unknown';
}

/** true, если вопрос относится к указанному типу (с учётом default = choice). */
export function isQuestionOfKind(q: QuestionPayload, kind: Kind): boolean {
  return questionKind(q) === kind;
}

/** Варианты для choice/poll: top-level `options` (legacy/дубликат) либо `kindData.options`. */
export function choiceOptions(q: QuestionPayload): string[] {
  if (q.options) return q.options;
  const kd = q.kindData;
  if (kd && (isChoiceKindData(kd) || isPollKindData(kd))) return kd.options;
  return [];
}

/** Лимит длины открытого ответа (из kindData.open.maxLen), если задан. */
export function openMaxLen(q: QuestionPayload): number | undefined {
  const kd = q.kindData;
  return kd && isOpenKindData(kd) ? kd.maxLen : undefined;
}

/** Лимит длины слова word-вопроса (из kindData.word.maxLen), если задан (И8). */
export function wordMaxLen(q: QuestionPayload): number | undefined {
  const kd = q.kindData;
  return kd && isWordKindData(kd) ? kd.maxLen : undefined;
}

/** Столбцы match: { left, right } из kindData (пустые массивы при отсутствии). */
export function matchColumns(q: QuestionPayload): { left: string[]; right: string[] } {
  const kd = q.kindData;
  if (kd && isMatchKindData(kd)) return { left: kd.left, right: kd.right };
  return { left: [], right: [] };
}

/** Элементы order: kindData.items (пустой массив при отсутствии). */
export function orderItems(q: QuestionPayload): string[] {
  const kd = q.kindData;
  return kd && isOrderKindData(kd) ? kd.items : [];
}

/** Варианты multi: top-level `options` (дубль) либо kindData.options (пусто при отсутствии). */
export function multiOptions(q: QuestionPayload): string[] {
  if (q.options) return q.options;
  const kd = q.kindData;
  return kd && isMultiKindData(kd) ? kd.options : [];
}

/**
 * Индексы верных вариантов multi из reveal: top-level `correctOptions` (М5)
 * либо reveal.correct (MultiReveal). Пустой массив до/при отсутствии reveal.
 */
export function multiCorrectOptions(reveal: RevealPayload | null): number[] {
  if (!reveal) return [];
  if (Array.isArray(reveal.correctOptions)) return reveal.correctOptions;
  const rd = reveal.reveal;
  return rd && isMultiReveal(rd) ? rd.correct : [];
}

export function isPositions(
  m: ServerMessage,
): m is { type: 'positions'; payload: PositionsPayload } {
  return m.type === 'positions';
}

/** Гвард события run_dead (персональный dead-пуш, 0.14.3 S4). */
export function isRunDead(m: ServerMessage): m is { type: 'run_dead'; payload: RunDeadPayload } {
  return m.type === 'run_dead';
}

/**
 * Углы плитки run-вопроса из kindData.run.corners (пустой массив при
 * отсутствии/чужом kindData — превью редактора всегда задаёт corners).
 */
export function runCorners(q: QuestionPayload): string[] {
  const kd = q.kindData;
  return kd && isRunKindData(kd) ? kd.corners : [];
}

// ─── Тайп-гарды kindData / reveal-data ────────────────────────────────────────

export function isChoiceKindData(d: unknown): d is ChoiceKindData {
  return isRecord(d) && isStringArray(d.options);
}

export function isPollKindData(d: unknown): d is PollKindData {
  return isRecord(d) && isStringArray(d.options);
}

export function isOpenKindData(d: unknown): d is OpenKindData {
  if (!isRecord(d)) return false;
  return d.maxLen === undefined || isNumber(d.maxLen);
}

export function isMatchKindData(d: unknown): d is MatchKindData {
  return isRecord(d) && isStringArray(d.left) && isStringArray(d.right);
}

export function isOrderKindData(d: unknown): d is OrderKindData {
  return isRecord(d) && isStringArray(d.items);
}

export function isMultiKindData(d: unknown): d is MultiKindData {
  return isRecord(d) && isStringArray(d.options);
}

export function isRunKindData(d: unknown): d is RunKindData {
  return isRecord(d) && isStringArray(d.corners);
}

export function isOpenReveal(r: unknown): r is OpenReveal {
  return isRecord(r) && isStringArray(r.accept);
}

export function isMatchReveal(r: unknown): r is MatchReveal {
  if (!isRecord(r) || !Array.isArray(r.pairs)) return false;
  return r.pairs.every((p) => isRecord(p) && isString(p.left) && isString(p.right));
}

export function isOrderReveal(r: unknown): r is OrderReveal {
  return isRecord(r) && isNumberArray(r.correctOrder);
}

export function isWordKindData(d: unknown): d is WordKindData {
  if (!isRecord(d)) return false;
  return d.maxLen === undefined || isNumber(d.maxLen);
}

export function isWordReveal(r: unknown): r is WordReveal {
  if (!isRecord(r) || !Array.isArray(r.words)) return false;
  return r.words.every(isWordCountEntry);
}

export function isMultiReveal(r: unknown): r is MultiReveal {
  return isRecord(r) && isNumberArray(r.correct);
}

/** Гвард reveal-данных run: {dead:[…]} — dead-лист погибших ведущему (0.14.1). */
export function isRunDeadReveal(r: unknown): r is RunDeadReveal {
  if (!isRecord(r) || !Array.isArray(r.dead)) return false;
  return r.dead.every(
    (d) =>
      isRecord(d) &&
      isNumber(d.playerId) &&
      isString(d.nick) &&
      isNumber(d.diedAtMs) &&
      isNumber(d.x) &&
      isNumber(d.y),
  );
}

/**
 * Строит wire-`AnswerPayload` из унифицированного `PlayerAnswer` (протокол §8.2):
 * choice/poll → `{questionId, option}`; open/match/order/multi →
 * `{questionId, kind, data}` (multi — data.selected, спека §2.1).
 */
export function makeAnswerPayload(questionId: number, answer: PlayerAnswer): AnswerPayload {
  switch (answer.kind) {
    case 'choice':
    case 'poll':
      return { questionId, option: answer.option };
    case 'open':
      return { questionId, kind: 'open', data: { text: answer.data.text } };
    case 'match':
      return { questionId, kind: 'match', data: { pairs: answer.data.pairs } };
    case 'order':
      return { questionId, kind: 'order', data: { order: answer.data.order } };
    case 'word':
      return { questionId, kind: 'word', data: { word: answer.data.word } };
    case 'multi':
      return {
        questionId,
        kind: 'multi',
        data: { selected: [...answer.data.selected].sort((a, b) => a - b) },
      };
  }
}
